import { supabase } from '../lib/supabase'
import type { Periodo } from '@praticheenergia/domain'

export interface RigaLettura {
  contatoreId: number
  /** Solo per la lettura iniziale, che puo' cadere nell'anno prima: se c'e'
   * vince sull'anno passato a `salvaLetture`. */
  anno?: number
  mese: number
  valore: string
}

/** Il mese la cui lettura fa da base al periodo: stesso calendario di
 * `mesiDelPeriodo` nel motore (packages/ad1-engine/src/ingresso.ts). */
export function mesePrecedente(periodo: Periodo, anno: number): { anno: number; mese: number } {
  return periodo === 'semestre_2' ? { anno, mese: 6 } : { anno: anno - 1, mese: 12 }
}

export function mesiDelPeriodo(periodo: Periodo): number[] {
  if (periodo === 'semestre_1') return [1, 2, 3, 4, 5, 6]
  if (periodo === 'semestre_2') return [7, 8, 9, 10, 11, 12]
  return Array.from({ length: 12 }, (_, i) => i + 1)
}

/** Il valore resta stringa fino al database: nessun passaggio da `number`. */
export async function salvaLetture(anno: number, righe: readonly RigaLettura[]): Promise<void> {
  const compilate = righe.filter((r) => r.valore.trim() !== '')

  for (const r of compilate) {
    if (!/^-?\d+([.,]\d+)?$/.test(r.valore.trim())) {
      throw new Error(`Valore non numerico al mese ${r.mese}`)
    }
  }

  if (compilate.length === 0) return

  // Non un upsert di PostgREST: l'indice unico di lettura e' parziale e
  // `on_conflict` non sa nominarlo. La RPC le marca manuali e accettate.
  const { error } = await supabase.rpc('salva_letture', {
    p_righe: compilate.map((r) => ({
      contatoreId: r.contatoreId,
      anno: r.anno ?? anno,
      mese: r.mese,
      valore: r.valore.trim().replace(',', '.'),
    })),
  })
  if (error) throw new Error(error.message)
}

export interface ContatoreConLetture {
  readonly id: number
  readonly matricola: string
  readonly codice: string
  /** Letture gia' in kWh: il motore non chiede la lettura iniziale. */
  readonly kwhDiretti: boolean
  /** Lettura del mese precedente al periodo (`mesePrecedente`). */
  readonly iniziale: string | null
  /** Chiave: mese dell'anno d'imposta. Valore: stringa, mai number — e un valore fiscale. */
  readonly letture: Record<number, string>
}

interface RigaGrezza {
  id: number
  matricola: string
  codice: string
  kwh_diretti: boolean
  lettura: { anno: number; mese: number; valore: string }[] | null
}

export async function elencaContatoriConLetture(
  impiantoId: number,
  anno: number,
  periodo: Periodo,
): Promise<ContatoreConLetture[]> {
  // `lettura(...)` senza `!inner`: un contatore appena creato non ha letture e
  // deve comunque comparire nella griglia, altrimenti non lo si puo' compilare.
  // I filtri vanno sulla tabella referenziata, non sulla radice. Anno prima
  // compreso: la lettura iniziale del primo semestre e' dicembre.
  const { data, error } = await supabase
    .from('contatore')
    .select('id, matricola, codice, kwh_diretti, lettura(anno, mese, valore)')
    .eq('impianto_id', impiantoId)
    .in('lettura.anno', [anno - 1, anno])
    .eq('lettura.stato_accettazione', 'accettata')
    .order('id')
  if (error) throw new Error(error.message)

  const base = mesePrecedente(periodo, anno)
  return ((data ?? []) as RigaGrezza[]).map((c) => {
    const letture = c.lettura ?? []
    return {
      id: c.id,
      matricola: c.matricola,
      codice: c.codice,
      kwhDiretti: c.kwh_diretti,
      iniziale: letture.find((l) => l.anno === base.anno && l.mese === base.mese)?.valore ?? null,
      letture: Object.fromEntries(letture.filter((l) => l.anno === anno).map((l) => [l.mese, l.valore])),
    }
  })
}
